// ── Editable task fields (TPT9213) ──
//
// The one list of task fields a person can set in the web app, shared by the task forms:
// the New Task modal (components/create-task-modal.js — every create entry point: board
// header, sprint lanes, kanban columns, Epic Add Item, drawer Add Child) and the task drawer /
// standalone task page (components/task-detail.js, which re-exports taskEditableFields()).
// Both forms offer every field below; lib/task-fields.test.js holds that list to the API's
// writable columns (the PATCH /tasks/:taskKey body) and to each form's markup.
//
// Kept pure — no DOM, no api.js — so the rules (agent-specific fields, dependency problems,
// the dependency sprint floor) are node-testable. Copy goes through lib/i18n.js t().

import { t } from './i18n.js';

// Writable through the API but never offered by the task forms. Everything else the API
// returns on a task row is read-only (ids, timestamps, token counters, device attribution,
// child counts) or has its own endpoint (comments, dev links, subscriptions).
export const TASK_FIELD_EXCLUSIONS = Object.freeze({
  task_key: 'generated identifier — the API allocates it on create',
  display_order: 'board position — set by dragging cards',
});

// `group` is the form section a field sits in — main (title, description), details (the
// compact property grid), links (tags, dependencies, parent, objective) and agent (how the Task
// App runs the task). `requires` names the condition under which a form shows the control (both
// forms apply it the same way); null means always shown to a writer:
//   sprints       the project's Sprints toggle is on (C1334) — otherwise a create submits the
//                 default sprint and the drawer hides the field
//   agent:<id>    the task's agent is <id> (claude_model / codex_model / pi_model)
//   effort        the agent supports a reasoning effort (Claude, Codex)
//   bypass        the task's effective permission mode is Bypass (Cowboy scope)
const FIELDS = Object.freeze([
  { key: 'title', group: 'main', labelKey: 'taskField.title', control: 'text', requires: null },
  { key: 'description', group: 'main', labelKey: 'taskField.description', control: 'markdown', requires: null },
  { key: 'type', group: 'details', labelKey: 'taskField.type', control: 'select', requires: null },
  { key: 'status', group: 'details', labelKey: 'taskField.status', control: 'select', requires: null },
  { key: 'category', group: 'details', labelKey: 'taskField.category', control: 'select', requires: null },
  { key: 'priority', group: 'details', labelKey: 'taskField.sprint', control: 'select', requires: 'sprints' },
  { key: 'assignee', group: 'details', labelKey: 'taskField.assignee', control: 'member', requires: null },
  { key: 'urgency', group: 'details', labelKey: 'taskField.urgency', control: 'select', requires: null },
  { key: 'story_points', group: 'details', labelKey: 'taskField.storyPoints', control: 'number', requires: null },
  { key: 'due_date', group: 'details', labelKey: 'taskField.dueDate', control: 'date', requires: null },
  { key: 'tags', group: 'links', labelKey: 'taskField.tags', control: 'chips', requires: null },
  { key: 'dependencies', group: 'links', labelKey: 'taskField.dependencies', control: 'chips', requires: null },
  { key: 'parent_id', group: 'links', labelKey: 'taskField.parent', control: 'picker', requires: null },
  { key: 'is_objective', group: 'links', labelKey: 'taskField.objective', control: 'checkbox', requires: null },
  { key: 'agent_assignee', group: 'agent', labelKey: 'taskField.agent', control: 'select', requires: null },
  { key: 'claude_model', group: 'agent', labelKey: 'taskField.model', control: 'text', requires: 'agent:claude' },
  { key: 'codex_model', group: 'agent', labelKey: 'taskField.model', control: 'text', requires: 'agent:codex' },
  { key: 'pi_model', group: 'agent', labelKey: 'taskField.model', control: 'text', requires: 'agent:pi' },
  { key: 'effort', group: 'agent', labelKey: 'taskField.effort', control: 'select', requires: 'effort' },
  { key: 'claude_design_mode', group: 'agent', labelKey: 'taskField.designMode', control: 'checkbox', requires: 'agent:claude' },
  { key: 'agent_permission_mode', group: 'agent', labelKey: 'taskField.permissionMode', control: 'listbox', requires: null },
  { key: 'cowboy_mode', group: 'agent', labelKey: 'taskField.cowboyMode', control: 'select', requires: 'bypass' },
]);

// Every writable task field the forms edit, in form order: { key, group, labelKey, control,
// requires, create, edit }. `key` is the API body field; both forms offer every entry (create and edit are
// always true — the flags make that contract explicit for the parity tests). A fresh copy per
// call, so a caller can never mutate the shared list.
export function taskEditableFields() {
  return FIELDS.map(field => ({ ...field, create: true, edit: true }));
}

// Form sections in display order; taskEditableFields() entries carry one each.
export const TASK_FIELD_GROUPS = Object.freeze(['main', 'details', 'links', 'agent']);

// ── Agent fields ──

// Values for agent_assignee; '' in a select stands for null (the project's default agent).
export const AGENT_ASSIGNEES = Object.freeze(['claude', 'codex', 'pi', 'human']);
export const EFFORT_LEVELS = Object.freeze(['low', 'medium', 'high', 'max']);

const MODEL_FIELDS = Object.freeze({ claude: 'claude_model', codex: 'codex_model', pi: 'pi_model' });

// The model field the given agent reads, or null (project default agent, Human).
export function agentModelField(agent) {
  return MODEL_FIELDS[agent] || null;
}

// Reasoning effort applies to Claude and Codex runs (tasks.effort, TPT284).
export function agentHasEffort(agent) {
  return agent === 'claude' || agent === 'codex';
}

// Claude Design mode runs Claude Code's /design skill (tasks.claude_design_mode, C1207).
export function agentHasDesignMode(agent) {
  return agent === 'claude';
}

// Suggestions for the model inputs — a mirror of the Task App's static fallback lists
// (ai/todo/server/src/client/constants.js CLAUDE_MODELS / CODEX_MODELS / PI_MODELS). Only
// suggestions: the API stores any model id, and the Task App passes it to the agent's CLI.
export const MODEL_SUGGESTIONS = Object.freeze({
  claude: Object.freeze(['opusplan', 'claude-opus-4-8', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-fable-5', 'claude-opus-5', 'claude-sonnet-5']),
  codex: Object.freeze(['gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini']),
  pi: Object.freeze(['openrouter/anthropic/claude-3.5-sonnet', 'openrouter/openai/gpt-4o']),
});

export function agentLabel(agent) {
  return AGENT_ASSIGNEES.includes(agent) ? t(`agent.${agent}`) : t('agent.default');
}

export function effortLabel(level) {
  return EFFORT_LEVELS.includes(level) ? t(`effort.${level}`) : t('effort.inherit');
}

// A model input's value as saved: trimmed text, or null (the agent's default).
export function modelInputValue(text) {
  const value = String(text ?? '').trim();
  return value ? value : null;
}

// A story points input as saved: { value } with null for blank, or { error } for anything that
// is not a non-negative whole number (the API's own rule).
export function storyPointsInputValue(text) {
  const raw = String(text ?? '').trim();
  if (!raw) return { value: null };
  if (!/^\d+$/.test(raw)) return { error: t('taskField.storyPointsInvalid') };
  return { value: Number(raw) };
}

// ── Dependencies ──

// Path (for api.get) of GET /tasks/dependency-check: `keys` as one comma-separated value, plus the
// task they belong to (an existing task — enables the self, loop and parent checks) or, for a
// task not created yet, its chosen parent's id.
export function dependencyCheckPath(projectId, keys, { taskKey = null, parentId = null } = {}) {
  const params = new URLSearchParams({ dependencies: (keys || []).join(',') });
  if (taskKey) params.set('task_key', taskKey);
  if (Number.isInteger(parentId) && parentId > 0) params.set('parent_id', String(parentId));
  return `/projects/${projectId}/tasks/dependency-check?${params}`;
}

// The lowest sprint a task may sit in, given its dependencies' check results (GET
// /tasks/dependency-check) or picked search rows: one past the highest sprint of any open
// dependency. Backlog (priority <= 0) and closed dependencies impose nothing — the rule the
// Task App and the MCP tools apply (minStepForDeps()). Null when nothing constrains.
export function dependencyFloor(entries) {
  let best = null;
  for (const entry of entries || []) {
    if (!entry || entry.problem || entry.closed) continue;
    const priority = Number(entry.priority);
    if (!Number.isFinite(priority) || priority <= 0) continue;
    if (!best || priority > best.priority) best = { key: entry.task_key, priority };
  }
  return best ? { ...best, floor: best.priority + 1 } : null;
}

// A chosen sprint moved up to the dependency floor (create form). Backlog (0) stays put, as on
// every other path that applies the floor. `moved` is null when nothing changed.
export function applyDependencyFloor(priority, floorInfo) {
  const p = Number(priority) || 0;
  if (p <= 0 || !floorInfo || p >= floorInfo.floor) return { priority: p, moved: null };
  return { priority: floorInfo.floor, moved: { from: p, to: floorInfo.floor, key: floorInfo.key, depPriority: floorInfo.priority } };
}

// The check results that stop a save: a problem on a key the user added. Keys already saved
// (`savedKeys`) are reported but never block, so an old dependency on a deleted task cannot
// lock the field — removing it is the fix.
export function blockingDependencyResults(results, savedKeys = []) {
  const saved = new Set((savedKeys || []).map(k => String(k).toUpperCase()));
  return (results || []).filter(r => r && r.problem && !saved.has(String(r.task_key).toUpperCase()));
}

// One sentence explaining a check result's problem, or '' for a valid dependency.
export function dependencyProblemText(result) {
  if (!result || !result.problem) return '';
  const key = result.task_key;
  switch (result.problem) {
    case 'not_found': return t('deps.notFound', { key });
    case 'self': return t('deps.self');
    case 'cycle': return t('deps.cycle', { key, path: (result.path || [key]).join(' → ') });
    case 'ancestor': return t('deps.ancestor', { key });
    default: return t('deps.invalid', { key });
  }
}
